import {
  Box,
  Button,
  Flex,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  Center,
  Alert,
  AlertIcon,
  Checkbox,
  IconButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useNavigate, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import { VendorName, vendorRepository } from 'api/vendorRepository';
import { z } from 'zod';
import { productSchema, flattenZodErrors } from 'validations/Validations';
import { useAuth } from 'contexts/authContext';

// ------------------- COMPONENT -------------------
export default function AddProductForm() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const inputBg = useColorModeValue('white', 'navy.700');
  const menuListBg = useColorModeValue('white', 'gray.800');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const { role, vendorId, user } = useAuth();
  const isVendor = role === "Vendor";

  // ---------- STATE ----------
  const [vendorNames, setVendorNames] = useState<VendorName[]>([]);
  const [productData, setProductData] = useState({
    selectedVendor: null,
    name: '',
    type: '',
    description: '',
    maintenanceSchedule: '',
    productStatus: '',
    location: '',
    installedDate: '',
    hasAMC: false,
    scheduledDate: '',
    productImage: '',
  });

  const [isAdding, setIsAdding] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] = useState<'success' | 'error' | 'warning'>('success');

  // const finalFrequency = productData.calibrationFrequency === 'Custom' ? productData.customFrequency : productData.calibrationFrequency;
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [productTypes, setProductTypes] = useState<{ id: string; type: string }[]>([]);
  // ---------- ALERT ----------
  const showCenteredAlert = (message: string, status: 'success' | 'error' | 'warning') => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 2000);
  };

  // ---------- HANDLE INPUT ----------
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let newValue = value;
    let newErrors = { ...errors };

    if (type === 'checkbox') {
      const isChecked = (e.target as HTMLInputElement).checked;
      setProductData(prev => ({ ...prev, [name]: isChecked }));
      return;
    }

    setProductData(prev => ({
      ...prev,
      [name]: newValue,
    }));

    try {
      if (name === 'selectedVendor') {
        const vendor = vendorNames.find(v => v.vendorId === newValue);

        const schemaField = productSchema.shape.selectedVendor;
        schemaField.parse(vendor || null);
        setProductData(prev => ({ ...prev, selectedVendor: vendor || null }));
      }
      else {

        if (name in productSchema.shape) {
          const fieldSchema = productSchema.shape[name as keyof typeof productSchema.shape];
          fieldSchema.parse(newValue);
        }

      }

      newErrors[name] = '';
    } catch (error) {
      if (error instanceof z.ZodError) {
        newErrors[name] = error.issues[0].message;
      }
    }
    setErrors(newErrors);
  }

  const handleVendorSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const vendorId = e.target.value;
    const vendor = vendorNames.find(v => v.vendorId === vendorId);
    setProductData(prev => ({ ...prev, selectedVendor: vendor || null }));
    setErrors(prev => ({ ...prev, selectedVendor: '' }));
  };

  const location = useLocation();

  const handleCancel = () => {
    const state = location.state as {
      source?: 'vendorDetail' | 'productList';
      vendorId?: string;
    };

    if (state?.source === 'vendorDetail' && state.vendorId) {
      navigate('/admin/vendors/vendorDetails', {
        state: { vendorId: vendorFromDetail.vendorId },
      });
    } else {
      navigate('/admin/vendors/VendorProduct');
    }
  };

  const vendorFromDetail = location.state as {
    vendorId?: string;
    vendorName?: string;
    source?: 'vendorDetail' | 'productList';
  } | null;


  // ---------- SUBMIT ----------
  const handleSubmit = async () => {

    if (isAdding) return;

    const productFormData = {
      ...productData,
      selectedVendor: productData.selectedVendor,
    };
    try {
      setErrors({});
      const validated = productSchema.parse(productFormData);

      const payload = {
        vendorId: validated.selectedVendor!.vendorId,
        vendorName: validated.selectedVendor!.name,
        products: [
          {
            productName: validated.name,
            productType: validated.type,
            productDescription: validated.description,
            productStatus: validated.productStatus,
            location: validated.location,
            hasAMC: validated.hasAMC,
            maintenanceSchedule: {
              maintenanceDetails: validated.maintenanceSchedule,
              status: "Scheduled",
              scheduledDate: validated.scheduledDate
                ? new Date(validated.scheduledDate)
                : new Date(),
            },
          },
        ],
      };

      setIsAdding(true);

      const response = await vendorRepository.addVendorProduct(payload);

      const vendorProductId = response?.vendorProductId;

      const productId = response?.productId;

      let uploadedImageUrl: string | null = null;

      if (imageFile && vendorProductId && productId) {
        uploadedImageUrl = await vendorRepository.uploadProductImage(
          vendorProductId,
          productId,
          imageFile
        );

        if (uploadedImageUrl) {
          setImagePreview(uploadedImageUrl);

          setProductData(prev => ({
            ...prev,
            productImage: uploadedImageUrl,
          }));
        }
      }
      showCenteredAlert('Vendor product added successfully', 'success');

      // Reset form
      setProductData({
        selectedVendor: null,
        name: '',
        type: '',
        description: '',
        maintenanceSchedule: '',
        productStatus: '',
        location: '',
        installedDate: '',
        hasAMC: false,
        scheduledDate: '',
        productImage: ''
      });

      setTimeout(() => {
        setImageFile(null);
        setImagePreview(null);
        if (vendorFromDetail?.vendorId) {
          navigate('/admin/vendors/vendorDetails', {
            state: { vendorId: vendorFromDetail.vendorId },
          });
        } else {
          navigate('/admin/vendors/VendorProduct');
        }
      }, 2000);

    } catch (error) {
      if (error instanceof z.ZodError) {
        // Flatten Zod errors into field-specific messages
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);
        showCenteredAlert('Please fill all required fields', 'warning');
     } else {
  const errorMessage =
    (error as any)?.response?.data ||
    (error as any)?.response?.data?.message ||
    'Failed to add product';

  showCenteredAlert(errorMessage, 'error');
}
    } finally {
      setIsAdding(false);
    }
  };

  // ---------- FETCH VENDORS ----------
  useEffect(() => {
    const setupData = async () => {
      // VENDORS
      if (vendorFromDetail?.vendorId) {
        const fixedVendor = {
          vendorId: vendorFromDetail.vendorId,
          name: vendorFromDetail.vendorName || "Vendor",
        };
        setVendorNames([fixedVendor]);
        setProductData(prev => ({ ...prev, selectedVendor: fixedVendor }));
      } else if (isVendor && vendorId) {
        const vendorDetails = await vendorRepository.getVendorById(vendorId);
        setVendorNames([{ vendorId, name: vendorDetails?.name || "Unknown Vendor" }]);
        setProductData(prev => ({ ...prev, selectedVendor: { vendorId, name: vendorDetails?.name || "Unknown Vendor" } }));
      } else {
        const data = await vendorRepository.getVendorNames();
        setVendorNames(data);
      }

      // PRODUCT TYPES
      try {
        const types = await vendorRepository.getProductTypes();
        setProductTypes(types);
      } catch (error) {
        showCenteredAlert("Failed to load product types", "error");
      }
    };

    setupData();
  }, [isVendor, vendorId, vendorFromDetail]);
  const isVendorLocked = isVendor || !!vendorFromDetail?.vendorId;

  // ------------------- JSX -------------------
  return (
    <Flex direction="column" minH="100vh" overflow="hidden" align="center" justify="flex-start" position="relative" px={{ base: '20px', md: '40px' }} pb="60px">
      <Box position="absolute" top="90" w="100%" h="40vh" bgGradient="linear(to-b, brand.400, brand.600)" borderRadius="20px" />
      <Card w={{ sm: '330px', md: '700px', lg: '850px' }} p="30px" mt={{ md: '150px' }} mx="auto" zIndex={1} overflowY="auto" borderRadius="16px">
        <Flex justify="space-between" align="center" mb="20px">
          <Text color={textColor} fontSize="2xl" fontWeight="700">
            Add Vendor Product
          </Text>
          <IconButton aria-label="Close" icon={<CloseIcon />} variant="ghost" size="sm" onClick={handleCancel} />
        </Flex>

        <Flex direction="column" w="100%">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
            <Stack direction="column" gap="20px">
              {user && (
                <FormControl isInvalid={!!errors.selectedVendor}>
                  <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                    Vendor Name
                  </FormLabel>
                  {isVendorLocked ? (
                    productData.selectedVendor ? (
                      <Input
                        value={productData.selectedVendor.name}
                        isReadOnly
                        h="44px"
                        fontSize="sm"
                        bg="gray.100"
                        mb={4}
                      />
                    ) : (
                      <Text>Loading vendor info...</Text>
                    )
                  ) : (
                    <Select
                      name="selectedVendor"
                      placeholder="Select vendor"
                      value={productData.selectedVendor?.vendorId || ''}
                      onChange={handleVendorSelectChange}
                      h="44px"
                      fontSize="sm"
                      mb={4}
                      bg={inputBg}
                      color={productData.selectedVendor ? textColor : placeholderColor}
                    >
                      {vendorNames.map(v => (
                        <option
                          key={v.vendorId}
                          value={v.vendorId}
                          style={{
                            backgroundColor: menuListBg,
                            color: textColor,
                          }}
                        >
                          {v.name}
                        </option>
                      ))}
                    </Select>
                  )}
                  {errors.selectedVendor && <FormErrorMessage>{errors.selectedVendor}</FormErrorMessage>}
                </FormControl>

              )}
              <FormControl isInvalid={!!errors.name}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>Product Name</FormLabel>
                <Input
                  name="name"
                  placeholder="e.g. Digital Meter"
                  value={productData.name}
                  onChange={handleInputChange}
                  h="44px"
                  fontSize="sm"
                  _dark={{ color: "white" }}
                  mb={4}
                />
                {errors.name && <FormErrorMessage>{errors.name}</FormErrorMessage>}
              </FormControl>

              <FormControl isInvalid={!!errors.productStatus}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>Product Status</FormLabel>


                <Select
                  name="productStatus"
                  placeholder="Select status"
                  h="44px"
                  fontSize="sm"
                  value={productData.productStatus}
                  onChange={handleInputChange}
                  mb={4}
                  bg={inputBg}
                  color={productData.productStatus ? textColor : "gray.400"}
                >
                  <option style={{ backgroundColor: menuListBg, color: textColor }}>Active</option>
                  <option style={{ backgroundColor: menuListBg, color: textColor }}>Inactive</option>
                </Select>
                {errors.productStatus && <FormErrorMessage>{errors.productStatus}</FormErrorMessage>}
              </FormControl>
            </Stack>

            <Stack direction="column" gap="20px">
              <FormControl isInvalid={!!errors.type}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>Product Type</FormLabel>

                <Select
                  name="type"
                  placeholder="Select type"
                  h="44px"
                  fontSize="sm"
                  value={productData.type}
                  onChange={handleInputChange}
                  mb={4}
                  bg={inputBg}

                >
                  {productTypes.map(pt => (
                    <option
                      key={pt.id}
                      value={pt.type}
                      style={{
                        backgroundColor: menuListBg,
                        color: textColor,
                      }}
                    >
                      {pt.type}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Product Image
                </FormLabel>

                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];


                      if (!file.type.startsWith("image/")) {
                        showCenteredAlert("Only image files allowed", "warning");
                        return;
                      }

                      if (file.size > 5 * 1024 * 1024) {
                        showCenteredAlert("File size must be less than or equal to 5MB", "warning");
                        return;
                      }

                      setImageFile(file);
                      setImagePreview(URL.createObjectURL(file));
                    }
                  }}
                  h="44px"
                  fontSize="sm"
                  mb={4}
                  color={textColor}
                />

                {/* Preview */}
                {/* {imagePreview && ( */}
                {imagePreview && (
                  <Box mt="10px" textAlign="center">
                    <img
                      src={imagePreview}
                      alt="preview"
                      style={{
                        width: "150px",
                        height: "150px",
                        objectFit: "cover",
                        borderRadius: "10px",
                        border: "1px solid #ddd",
                      }}
                    />
                  </Box>
                )}

              </FormControl>
              <FormControl isInvalid={!!errors.description}>
                <FormLabel fontSize="sm" fontWeight="bold" mb="8px" color={textColor} >Product Description</FormLabel>
                <Input
                  name="description"
                  placeholder="Short description"
                  value={productData.description}
                  onChange={handleInputChange}
                  h="44px"
                  _dark={{ color: "white" }}
                  fontSize="sm"
                  mb={4}
                />
                {errors.description && <FormErrorMessage>{errors.description}</FormErrorMessage>}
              </FormControl>
            </Stack>
          </SimpleGrid>

          <Flex mt="4" mb="4">
            <Box>
              <Checkbox
                name="hasAMC"
                isChecked={productData.hasAMC}
                onChange={handleInputChange}
                colorScheme="brand"
                fontWeight="normal"
                fontSize="sm"
                lineHeight="20px"
              >
                Is AMC required for this product?
              </Checkbox>
            </Box>
          </Flex>

          <Flex justify="center" gap="20px" mt="24px">
            <Button
              variant="darkBrand"
              fontSize="sm"
              borderRadius="16px"
              w={{ base: '128px', md: '148px' }}
              h="46px" onClick={handleSubmit}
              isDisabled={isAdding}
              isLoading={isAdding}>
              Submit
            </Button>
            <Button variant="outline" fontSize="sm" borderRadius="16px" w={{ base: '128px', md: '148px' }} h="46px" borderColor="gray.300" onClick={handleCancel}>
              Cancel
            </Button>
          </Flex>
        </Flex>
      </Card>

      {centerAlertVisible && (

        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            opacity={1}
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={2}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}